import { create } from 'zustand';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import {
  toQueryString,
  type PaginationQueryParamsWithStatus
} from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { PersonaEntrega, PersonaEntregaResumen } from './persona-entrega';

export interface PersonalEntregaState {
  list: PaginatedList<PersonaEntregaResumen>;
  current: PersonaEntrega;
  actives: PersonaEntregaResumen[];

  nuevo: () => void;
  cargarPaginadas: (params: PaginationQueryParamsWithStatus) => Promise<void>;
  cargarActivas: () => Promise<void>;
  cargarPorId: (id: number) => Promise<void>;
  registrar: (request: RegistrarPersonaEntrega) => Promise<number | null>;
  actualizar: (id: number, request: ActualizarPersonaEntrega) => Promise<boolean>;
  activar: (id: number) => Promise<boolean>;
  desactivar: (id: number) => Promise<boolean>;
}

export interface RegistrarPersonaEntrega {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

export interface ActualizarPersonaEntrega {
  nombre: string;
  idCiudad: number;
  idEstado: number;
  direccion?: string;
  telefono1?: string;
  telefono2?: string;
  notas?: string;
}

const { showSuccessToast } = useUIStore.getState();

export const usePersonalEntregaStore = create<PersonalEntregaState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: {} as PersonaEntrega,
  actives: [],

  nuevo: () =>
    set({
      current: {} as PersonaEntrega
    }),
  cargarPaginadas: async (params: PaginationQueryParamsWithStatus) => {
    const data = await makeGet<PaginatedList<PersonaEntregaResumen>>(
      `/personal-entrega/paginados?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarActivas: async () => {
    const data = await makeGet<Array<PersonaEntregaResumen>>('/personal-entrega/activos');

    if (data.isFailure) return;

    set({
      actives: data.result
    });
  },
  cargarPorId: async (id: number) => {
    const data = await makeGet<PersonaEntrega>(`/personal-entrega/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  registrar: async (request: RegistrarPersonaEntrega) => {
    const data = await makePost<RegistrarPersonaEntrega, number | null>(
      '/personal-entrega/',
      request
    );

    if (data.isFailure) return null;

    showSuccessToast('Registro agregado');

    set({
      current: {} as PersonaEntrega
    });

    return data.result;
  },
  actualizar: async (id: number, request: ActualizarPersonaEntrega) => {
    const data = await makePatch<ActualizarPersonaEntrega, PersonaEntrega>(
      `/personal-entrega/${id}`,
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Registro actualizado');

    set({
      current: {
        ...get().current,
        ...request
      }
    });

    return true;
  },
  activar: async (id: number) => {
    const data = await makePatch(`/personal-entrega/${id}/activar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro activado');

    set({
      current: {
        ...get().current,
        activa: true
      }
    });

    return true;
  },
  desactivar: async (id: number) => {
    const data = await makePatch(`/personal-entrega/${id}/desactivar`);

    if (data.isFailure) return false;

    showSuccessToast('Registro desactivado');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  }
}));
